import {SearchIcon} from '@primer/octicons-react'
import {announce} from '@github-ui/aria-live'
import {useEffect, useRef} from 'react'
import type {Delta, ToolArgs, ToolExecutionState} from '../../../types/session'
import {
  formatCodeSearchResultCount,
  getCodeSearchDisplay,
  type CodeSearchDisplay,
} from '../../../utils/code-search-display'
import {Tool, ToolCodeOutput, MarkdownLoading, parseContent} from './ToolPrimitives'
import styles from './CodeSearchTool.module.css'

type CodeSearchToolProps = {
  name: string
  args: ToolArgs
  delta: Delta
  detailedContent: string | undefined
  executionState?: ToolExecutionState
}

export function announceSettledCodeSearch({name, args, delta, detailedContent, executionState}: CodeSearchToolProps) {
  const {isError: hasErrorContent, content} = parseContent(delta.content)
  const display = getCodeSearchDisplay({args, content, detailedContent, toolName: name})
  const query = display.query ?? 'unknown query'
  const repository = display.repository ? ` in ${display.repository}` : ''
  if (executionState === 'failed' || hasErrorContent) {
    const error = content ? ` ${content}` : ''
    announce(`GitHub search failed for ${query}${repository}.${error}`, {assertive: true})
    return
  }

  const count = display.resultCount ? ` ${formatCodeSearchResultCount(display.resultCount)}.` : ''
  announce(`Searched GitHub for ${query}${repository}.${count}`)
}

export function CodeSearchTool({
  name,
  args,
  delta,
  detailedContent,
  isInGroup,
  executionState,
}: CodeSearchToolProps & {isInGroup?: boolean}) {
  const {isError: hasErrorContent, content} = parseContent(delta.content)
  const isError = executionState === 'failed' || hasErrorContent
  const isPending = executionState ? executionState === 'running' : content === undefined
  const isSettled = !isPending
  const display = getCodeSearchDisplay({args, content, detailedContent, toolName: name})
  const query = display.query ?? 'unknown query'
  const title = isInGroup
    ? `${query}${display.repository ? ` in ${display.repository}` : ''}`
    : `${isError ? 'GitHub search failed' : isPending ? 'Searching GitHub' : 'Searched GitHub'} for`
  const resultCount = !isPending && !isError && display.resultCount ? display.resultCount : null
  const wasSettledRef = useRef(isSettled)

  useEffect(() => {
    const wasSettled = wasSettledRef.current
    wasSettledRef.current = isSettled
    if (wasSettled || !isSettled) return
    announceSettledCodeSearch({name, args, delta, detailedContent, executionState})
  }, [args, delta, detailedContent, executionState, isSettled, name])

  const titleSuffix = !isInGroup ? (
    <span className="fgColor-muted text-small">
      <span>{query}</span>
      {display.repository ? (
        <>
          {' '}
          in <span className="text-mono">{display.repository}</span>
        </>
      ) : null}
      {resultCount ? <span> · {formatCodeSearchResultCount(resultCount)}</span> : null}
    </span>
  ) : undefined

  return (
    <Tool
      title={title}
      isError={isError}
      isPending={isPending}
      isInGroup={isInGroup}
      inlineTitleSuffix
      toolHeaderProps={{
        leadingVisual: isInGroup ? undefined : <SearchIcon aria-hidden="true" />,
        titleSuffix,
      }}
    >
      {isError ? <ToolCodeOutput output={content} /> : <CodeSearchOutput display={display} />}
    </Tool>
  )
}

function CodeSearchOutput({display}: {display: CodeSearchDisplay}) {
  if (display.content === undefined) return <MarkdownLoading />

  const hasContent = display.content.length > 0

  return (
    <div className="tmp-p-3">
      {hasContent ? (
        <div className="border rounded bgColor-default overflow-hidden">
          <pre className={`m-0 tmp-px-3 py-2 text-mono text-small ${styles.content}`}>{display.content}</pre>
        </div>
      ) : (
        <div className="fgColor-muted text-small">
          {display.resultCount === '0' ? 'No results found.' : 'No result details returned.'}
        </div>
      )}
      {display.warnings.length > 0 || display.advisory ? (
        <div className="tmp-mt-3 text-small">
          {display.warnings.length > 0 ? (
            <>
              <strong className="fgColor-attention">Search warnings</strong>
              <ul className="mt-1 mb-2 pl-4">
                {display.warnings.map(warning => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </>
          ) : null}
          {display.advisory ? <p className="m-0 fgColor-muted">{display.advisory}</p> : null}
        </div>
      ) : null}
    </div>
  )
}
